// in src/contacts.js
import React from 'react';
import PropTypes from 'prop-types';
import {
  Create,
  Datagrid,
  DateField,
  DateInput,
  DeleteButton,
  DisabledInput,
  Edit,
  EditButton,
  Filter,
  FunctionField,
  List,
  NumberField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput
} from 'admin-on-rest/lib/mui';
import MUITextField from 'material-ui/TextField';
import get from 'lodash.get';
import { Field } from 'redux-form';
import isString from 'lodash.isstring';
import isObject from 'lodash.isobject';
import { STATUSES } from './helpers/contacts';
import {
  Card,
  CardTitle,
  CardText,
  AutoComplete,
  FlatButton
} from 'material-ui';
import AutoCompleteWithInput from './components/AutoCompleteWithInput';
import fetcher from './helpers/fetcher';
import { URL_PROJECT_CONTACT_FIELDS, URL_PROJECTS } from './constants';
import DataField from './components/DataField';

const renderTextField = ({
  input,
  label,
  meta: { touched, error },
  ...custom
}) => (
  <MUITextField
    hintText={label}
    floatingLabelText={label}
    errorText={touched && error}
    id={`textFieldFor_${label}`}
    {...input}
    {...custom}
  />
);

const CustomFieldInput = () => (
  <span key="customField">
    <Field name="fieldName" component={renderTextField} label="Name" />
    &nbsp;
    <Field name="fieldValue" component={renderTextField} label="Value" />
  </span>
);

const ContactFilter = props => (
  <Filter {...props}>
    <TextInput
      label="Search all"
      source="q"
      alwaysOn
      format={data => (data || '').trim()}
    />
    <TextInput
      label="Numbers"
      source="re"
      alwaysOn
      format={data => (data || '').trim()}
    />
    <CustomFieldInput label="Custom" source="fieldName" />
    <TextInput
      label="Search"
      source="q_or_all"
      parse={searchText => ({
        or: [
          { firstName: { like: `${searchText}%` } },
          { lastName: { like: `${searchText}%` } },
          { email: { like: `${searchText}%` } },
          { phone: { like: `${searchText}%` } }
        ]
      })}
      format={data => get(data, 'or[0].firstName.like', '').replace(/%$/, '')}
    />
    <TextInput
      label="Email"
      source="email"
      parse={searchText => ({
        email: { like: `${searchText}%` }
      })}
      format={data => get(data, 'email.like', '').replace(/%$/, '')}
    />
    <TextInput
      label="Phone"
      source="phone"
      parse={searchText => ({
        phone: { like: `${searchText}%` }
      })}
      format={data => get(data, 'phone.like', '').replace(/%$/, '')}
    />
    <TextInput
      label="Name"
      source="q_or_name"
      parse={searchText => ({
        or: [
          { firstName: { like: `${searchText}%` } },
          { lastName: { like: `${searchText}%` } }
        ]
      })}
      format={data => get(data, 'or[0].firstName.like', '').replace(/%$/, '')}
    />
    <ReferenceArrayInput
      label="Project"
      source="project"
      reference="projects"
      filterToQuery={searchText => ({ name: { like: '%' + searchText + '%' } })}
    >
      <SelectArrayInput
        optionText={r => `${r.name}`}
        options={{
          onBlur: () => {},
          anchorOrigin: { horizontal: 'right', vertical: 'bottom' },
          targetOrigin: { horizontal: 'right', vertical: 'top' },
          listStyle: {
            width: 'auto'
          }
        }}
      />
    </ReferenceArrayInput>
    <SelectArrayInput
      label="Status"
      source="status"
      choices={STATUSES.map(status => ({
        id: status,
        name: status
      }))}
      options={{ onBlur: () => {} }}
    />

    {/* <ReferenceInput label="Current Agent" source="user" reference="users">
      <SelectInput optionText={r => `${r.username}`} />
    </ReferenceInput> */}
    <ReferenceArrayInput
      label="Agent"
      source="updatedByAgent"
      reference="users"
      filterToQuery={searchText => ({
        username: { like: '%' + searchText + '%' }
      })}
    >
      <SelectArrayInput optionText={r => `${r.username}`} />
    </ReferenceArrayInput>
    <DateInput
      source="updatedAtAfter"
      label="Updated after"
    />
    <DateInput
      source="updatedAtBefore"
      label="Updated before"
    />
  </Filter>
);

export const ContactList = props => (
  <List
    {...props}
    filters={<ContactFilter />}
    sort={{ field: 'updatedAt', order: 'DESC' }}
  >
    <Datagrid>
      <ReferenceField label="Project" source="project" reference="projects">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        label="Agent"
        source="updatedByAgent"
        reference="users"
        allowEmpty
      >
        <TextField source="username" />
      </ReferenceField>
      <DataField label="data" />
      <TextField source="firstName" />
      <TextField source="lastName" />
      <TextField source="phone" />
      <TextField source="status" label="Current status" />
      <NumberField source="callsNumber" label="Calls" sortable={false} />
      <DateField source="callbackDate" showTime />
      <DateField source="updatedAt" showTime />
      <EditButton />
      <DeleteButton />
    </Datagrid>
  </List>
);

const ContactTitle = ({ record }) => {
  return (
    <span>
      Contact {record ? `"${record.firstName} ${record.lastName}"` : ''}
    </span>
  );
};

ContactTitle.propTypes = {
  record: PropTypes.object
};

const ObjectData = ({ onChange, data, fields }) => (
  <div>
    {(fields || Object.keys(data) || []).map(key => {
      const renderData = data[key];
      const localOnChange = changedData =>
        onChange({ ...data, [key]: changedData });

      if (isObject(renderData)) {
        return (
          <Card>
            <CardTitle title={key} />
            <CardText>
              <ObjectData
                key={`key_${key}`}
                data={renderData}
                onChange={localOnChange}
              />
            </CardText>
          </Card>
        );
      }
      if (Array.isArray(renderData)) {
        return (
          <Card>
            <CardTitle title={key} />
            <CardText>
              <ArrayData
                key={`key_${key}`}
                data={renderData}
                onChange={localOnChange}
              />
            </CardText>
          </Card>
        );
      }
      if (key === 'status') {
        return (
          <div key={`key_${key}`}>
            <AutoCompleteWithInput
              hintText={key}
              dataSource={STATUSES}
              value={renderData}
              filter={AutoComplete.noFilter}
              onChange={value => {
                localOnChange(value);
              }}
            />
          </div>
        );
      }
      return (
        <div key={`key_${key}`}>
          <MUITextField
            floatingLabelText={key}
            value={renderData}
            onChange={(e, value) => {
              e.preventDefault();
              localOnChange(value);
            }}
          />
        </div>
      );
    })}
  </div>
);
ObjectData.propTypes = {
  data: PropTypes.object,
  fields: PropTypes.array,
  onChange: PropTypes.func
};

const ArrayData = ({ onChange, data }) =>
  data.map((dataItem, index) => (
    <Card key={`array_item_${index}`}>
      <CardTitle title={index + 1} />
      <CardText>
        <ObjectData
          key={`key_${index}`}
          data={dataItem}
          onChange={changedData => {
            const newData = [...data];
            newData[index] = changedData;
            onChange(newData);
          }}
        />
      </CardText>
    </Card>
  ));

ArrayData.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func
};

class DataFieldInput extends React.Component {
  constructor(props) {
    super(props);
    this.state = { fields: null };
  }
  componentDidMount() {
    const projectId = get(
      this.props.record,
      'project.id',
      get(this.props.record, 'project')
    );
    fetcher.get(`${URL_PROJECTS}/${projectId}`).then(data => {
      const fields = get(data, 'data.data.meta.callScreenFormData.fields');
      this.setState({ fields: (fields || []).map(field => field.identifier) });
    });
  }

  _onChange = data => {
    this.props.input.onChange(data);
  };

  render() {
    const data = this.props.input.value;
    return (
      <Card>
        <CardTitle title={`Data`} />
        <CardText>
          <ObjectData
            data={data}
            fields={this.state.fields}
            onChange={this._onChange}
          />
        </CardText>
      </Card>
    );
  }
}

DataFieldInput.defaultProps = {
  addField: true
};
DataFieldInput.propTypes = {
  input: TextInput.propTypes.input,
  record: TextInput.propTypes.record
};

class CallsFieldInput extends React.Component {
  _onChange = data => {
    this.props.input.onChange(data);
  };

  _onAddPress = () => {
    const data = this.props.input.value || [];
    data.push({
      status: '',
      details: {
        subject: '',
        start: new Date(),
        callbackDate: new Date()
      }
    });
    this.props.input.onChange([...data]);
  };

  render() {
    const data = this.props.input.value;
    return (
      <Card>
        <CardTitle title={`Calls`} />
        {Array.isArray(data) && (
          <CardText>
            <ArrayData data={data} onChange={this._onChange} />
          </CardText>
        )}
        {!Array.isArray(data) && (
          <FlatButton
            label="Add call (to set status)"
            onClick={this._onAddPress}
          />
        )}
      </Card>
    );
  }
}

CallsFieldInput.defaultProps = {
  addField: true
};
CallsFieldInput.propTypes = {
  input: TextInput.propTypes.input
};

export const ContactEdit = props => (
  <Edit title={<ContactTitle />} {...props}>
    <SimpleForm>
      <DisabledInput source="id" />
      <ReferenceField label="Project" source="project" reference="projects">
        <TextField source="name" />
      </ReferenceField>
      <ReferenceField
        label="Agent"
        source="updatedByAgent"
        reference="users"
        allowEmpty
      >
        <TextField source="username" />
      </ReferenceField>
      <TextField source="firstName" />
      <TextField source="lastName" />
      <TextField source="phone" />
      <TextField source="email" />
      <DisabledInput
        source="status"
        label="Status (computed from last call status)"
      />
      <DataFieldInput source="data" />
      <CallsFieldInput source="calls" />
    </SimpleForm>
  </Edit>
);

export const ContactCreate = props => (
  <Create {...props}>
    <SimpleForm>
      <ReferenceInput label="Project" source="project" reference="projects">
        <SelectInput optionText="name" />
      </ReferenceInput>
      <TextInput source="firstName" />
      <TextInput source="lastName" />
    </SimpleForm>
  </Create>
);



// WEBPACK FOOTER //
// ./src/contacts.js